import { ClerkProvider, Protect, RedirectToTasks, SignedIn, SignedOut } from "@clerk/clerk-react";
import CloseIcon from "@mui/icons-material/Close";
import { CssBaseline, ThemeProvider } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { closeSnackbar, SnackbarProvider } from "notistack";
import posthog from "posthog-js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router";
import App from "./App.tsx";
import { ErrorBoundary } from "./components/error-boundary.tsx";
import { VibeTesting } from "./components/vibe-qa/vibe-it.tsx";
import AnalyticsPage from "./pages/analytics/index.tsx";
import ArgusPage from "./pages/argus.tsx";
import AuthChooseOrgPage from "./pages/auth-choose-org.tsx";
import BrowserSessionDetailsPage from "./pages/browser-session-details.tsx";
import BrowserSessionsPage from "./pages/browser-sessions.tsx";
import ChatPage from "./pages/chat.tsx";
import CheckDetailsPage from "./pages/check-details.tsx";
import CheckScheduleDetailsPage from "./pages/check-schedule-details.tsx";
import CheckSchedulesPage from "./pages/check-schedules.tsx";
import ChecksPage from "./pages/checks.tsx";
import ConfigurationPage from "./pages/configuration.tsx";
import { DemoLanding1Page } from "./pages/demo/landing-1.tsx";
import { DemoReportPage } from "./pages/demo/report.tsx";
import DiscoveryReviewPage from "./pages/discovery-review-page.tsx";
import DiscoveryRunDetailsRouter from "./pages/discovery-run-details-router.tsx";
import DiscoveryRunsPage from "./pages/discovery-runs.tsx";
import NewGeneratePage from "./pages/generate-fast.tsx";
import GraphPage from "./pages/graph.tsx";
import { IntegrationsConnectedPage } from "./pages/integrations-connected.tsx";
import { IntegrationsGitHubSetupPage } from "./pages/integrations-github-setup.tsx";
import { IntegrationsSlackSetupPage } from "./pages/integrations-slack-setup.tsx";
import IssueDetailPage from "./pages/issue-detail.tsx";
import IssuesPage from "./pages/issues.tsx";
import LineagePage from "./pages/lineage.tsx";
import MagicPage from "./pages/magic.tsx";
import MainPage from "./pages/main.tsx";
import { NotFoundPage } from "./pages/not-found.tsx";
import OldMainPage from "./pages/old-main.tsx";
import OnboardingPage from "./pages/onboarding.tsx";
import { OnboardingUnsubscribedPage } from "./pages/onboarding-unsubscribed.tsx";
import PersonasPage from "./pages/personas.tsx";
import RunDetailsPage from "./pages/run-details.tsx";
import RunsPage from "./pages/runs.tsx";
import StatusPage from "./pages/status-page.tsx";
import SuiteRunDetailsPage from "./pages/suite-run-details.tsx";
import SuiteRunsPage from "./pages/suite-runs.tsx";
import TestCasesPage from "./pages/test-cases.tsx";
import TestChatDetailsPage from "./pages/test-chat-details.tsx";
import TestChatsPage from "./pages/test-chats.tsx";
import TestDetailsPage from "./pages/test-details.tsx";
import { TestErrorPage } from "./pages/test-error.tsx";
import TestSuiteDetailsPage from "./pages/test-suite-details.tsx";
import TestSuitesPage from "./pages/test-suites.tsx";
import TestsMainPage from "./pages/tests-main.tsx";
import { PublicVibeQA } from "./pages/vibe-qa/public.tsx";
import { ClerkTokenProvider } from "./providers/clerk-token.tsx";
import { UserPreferencesProvider } from "./providers/user-preferences-provider.tsx";
import { theme } from "./theme/theme.ts";

import "./styles.css";
import SignInPage from "./pages/sign-in.tsx";
import SignUpPage from "./pages/sign-up.tsx";

// Import your Publishable Key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

if (import.meta.env.PROD) {
  posthog.init("phc_HuxMOzFDDzWUlpige7JXGxFMF9dYg8ChpIzoB9ywKyZ", {
    api_host: "https://tt.desplega.sh",
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      sampleRate: 0.1,
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive="true"]',
    },
    capture_performance: false,
    capture_dead_clicks: false,
    enable_heatmaps: false,
  });

  Sentry.init({
    dsn: "https://2cf45799984ffb67fa9e6a06865ba75f@o4510784970293248.ingest.de.sentry.io/4510784971800656",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    // Avoid AdBlockers blocking Sentry
    tunnel: "/tunnel",
    release: `app@${__APP_VERSION__}`,
  });
}

/**
 * Legacy redirect: `/settings/argus-action` → `/configuration?tab=argus`.
 * Preserves the original query string (e.g. `?argus_session_id=…&argus_instance_id=…`)
 * seeded by the Argus onboarding skill (see `be/core/argus/workspace.py`).
 * The settings page itself was merged into the Configuration page as an "Argus" tab.
 */
const ArgusActionLegacyRedirect = () => {
  const { search } = useLocation();
  const existing = search.startsWith("?") ? search.slice(1) : search;
  const query = existing ? `tab=argus&${existing}` : "tab=argus";
  return <Navigate to={`/configuration?${query}`} replace />;
};

// Persist pricing UTM intent early (before auth/router can strip query params)
try {
  const search = window.location.search;
  if (search) {
    const params = new URLSearchParams(search);
    if (
      params.get("utm_source") === "pricing" &&
      params.get("utm_content") === "cta_button" &&
      !sessionStorage.getItem("pricing_utm_captured")
    ) {
      sessionStorage.setItem("pricing_utm_pending", "1");
      sessionStorage.setItem("pricing_utm_query", search);
      sessionStorage.removeItem("pricing_utm_captured");
      sessionStorage.removeItem("pricing_utm_shown");
    }
  }
} catch {
  // ignore errors in pre-router UTM stash (safe fallback)
}

const router = createBrowserRouter([
  // Public routes (no authentication required)
  {
    path: "/demo/landing-1",
    Component: DemoLanding1Page,
  },
  // Public routes (no authentication required)
  {
    path: "/demo/report/:reportId",
    Component: DemoReportPage,
  },
  {
    path: "/demo/vibe-qa",
    Component: PublicVibeQA,
  },
  {
    path: "/onboarding/unsubscribed",
    Component: OnboardingUnsubscribedPage,
  },
  {
    path: "/integrations/connected",
    Component: IntegrationsConnectedPage,
  },
  {
    path: "/integrations/slack/setup-channel",
    Component: IntegrationsSlackSetupPage,
  },
  {
    path: "/integrations/github/setup-repos",
    Component: IntegrationsGitHubSetupPage,
  },
  // Auth
  {
    path: "/sign-in",
    Component: SignInPage,
  },
  {
    path: "/sign-up",
    Component: SignUpPage,
  },
  {
    path: "/auth/turbo-magic",
    Component: AuthChooseOrgPage,
  },
  // Protected onboarding route (auth required, but NO dashboard chrome).
  // Phase 2 renders a stub; Phase 3 swaps the body for `<ArgusOnboarding />`.
  {
    path: "/onboarding",
    Component: OnboardingPage,
  },
  // Protected routes (authentication required)
  {
    Component: App,
    children: [
      {
        path: "/",
        Component: MainPage,
      },
      {
        path: "/old-home",
        Component: OldMainPage,
      },
      {
        path: "/generate",
        Component: NewGeneratePage,
      },
      {
        path: "/analytics",
        Component: AnalyticsPage,
      },
      {
        path: "/configuration",
        Component: ConfigurationPage,
      },
      {
        path: "/test-chats",
        Component: TestChatsPage,
      },
      {
        path: "/test-chats/:chatId",
        Component: TestChatDetailsPage,
      },
      {
        path: "/tests-main",
        Component: TestsMainPage,
      },
      // Redirect old tests route to test-cases
      {
        path: "/tests-main/tests",
        element: <Navigate to="/test-cases" replace />,
      },
      {
        path: "/tests-main/test-suites",
        Component: TestSuitesPage,
      },
      {
        path: "/discovery-runs",
        Component: DiscoveryRunsPage,
      },
      {
        path: "/discovery-runs/:runId",
        Component: DiscoveryRunDetailsRouter,
      },
      {
        path: "/discovery-review/:runId",
        Component: DiscoveryReviewPage,
      },
      {
        path: "/test-cases",
        Component: TestCasesPage,
      },
      {
        path: "/personas",
        Component: PersonasPage,
      },
      {
        path: "/personas/:personaId",
        Component: PersonasPage,
      },
      {
        path: "/tests-main/test-suites/:testSuiteId",
        Component: TestSuiteDetailsPage,
      },
      {
        path: "/tests-main/tests/:testId",
        Component: TestDetailsPage,
      },
      // Compatibility
      {
        path: "/test-suites/:testSuiteId",
        Component: TestSuiteDetailsPage,
      },
      {
        path: "/tests/:testId",
        Component: TestDetailsPage,
      },
      // Redirect old tests route to test-cases
      {
        path: "/tests",
        element: <Navigate to="/test-cases" replace />,
      },
      {
        path: "/runs",
        Component: RunsPage,
      },
      {
        path: "/runs/:runId",
        Component: RunDetailsPage,
      },
      {
        path: "/suite-runs",
        Component: SuiteRunsPage,
      },
      {
        path: "/suite-runs/:suiteRunId",
        Component: SuiteRunDetailsPage,
      },
      {
        path: "/checks",
        Component: ChecksPage,
      },
      {
        path: "/checks/:checkId",
        Component: CheckDetailsPage,
      },
      {
        path: "/check-schedules",
        Component: CheckSchedulesPage,
      },
      {
        path: "/check-schedules/:scheduleId",
        Component: CheckScheduleDetailsPage,
      },
      {
        path: "/status",
        Component: StatusPage,
      },
      {
        path: "/issues",
        Component: IssuesPage,
      },
      {
        path: "/issues/:issueId",
        Component: IssueDetailPage,
      },
      {
        path: "/graph",
        Component: GraphPage,
      },
      {
        path: "/lineage",
        Component: LineagePage,
      },
      {
        path: "/chats",
        Component: ChatPage,
      },
      {
        path: "/vibe-qa",
        Component: VibeTesting,
      },
      {
        path: "/error",
        Component: TestErrorPage,
      },
      {
        path: "/magic",
        Component: MagicPage,
      },
      {
        path: "/argus",
        Component: ArgusPage,
      },
      {
        // Legacy path — the Argus onboarding skill previously seeded this URL
        // into ~/drive/TODOS.md (see be/core/argus/workspace.py). The settings
        // now live under the "Argus" tab in /configuration; keep the redirect
        // so any stale TODOS links continue to work.
        path: "/settings/argus-action",
        Component: ArgusActionLegacyRedirect,
      },
      {
        path: "/browser-sessions",
        Component: BrowserSessionsPage,
      },
      {
        path: "/browser-sessions/:sessionId",
        Component: BrowserSessionDetailsPage,
      },
      {
        path: "*",
        Component: NotFoundPage,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error, {
        tags: { queryKey: JSON.stringify(query.queryKey) },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      Sentry.captureException(error, {
        tags: {
          mutationKey: JSON.stringify(mutation.options.mutationKey ?? "anonymous"),
        },
      });
    },
  }),
});
const PUBLIC_PATHS = ["/demo/*", "/onboarding/*", "/auth/turbo-magic", "/sign-*"];

const isPublicPath = (path: string) => {
  return PUBLIC_PATHS.some((publicPath) => {
    const regex = new RegExp(`^${publicPath.replace("*", ".*")}$`);
    return regex.test(path);
  });
};

const AuthWrapper = () => {
  const pathname = window.location.pathname;
  const isPublicRoute = isPublicPath(pathname);

  if (isPublicRoute) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SignedOut>
        <Protect treatPendingAsSignedOut={false} fallback={<SignInPage />}>
          <RedirectToTasks />
        </Protect>
      </SignedOut>
      <SignedIn>
        <RouterProvider router={router} />
      </SignedIn>
    </ThemeProvider>
  );
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <SnackbarProvider
        maxSnack={2}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        action={(snackbarId) => (
          <IconButton
            size="small"
            aria-label="close notification"
            onClick={() => closeSnackbar(snackbarId)}
            sx={{ color: "inherit" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      >
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          supportEmail="contact@desplega.ai"
          afterSignOutUrl="/"
          allowedRedirectOrigins={[
            "http://localhost:5173",
            "http://localhost:3000",
            "http://localhost:3001",
            "https://tester.desplega.ai",
          ]}
          taskUrls={{
            "choose-organization": "/auth/turbo-magic",
          }}
        >
          <QueryClientProvider client={queryClient}>
            <ClerkTokenProvider>
              <UserPreferencesProvider>
                <AuthWrapper />
              </UserPreferencesProvider>
            </ClerkTokenProvider>
          </QueryClientProvider>
        </ClerkProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  </StrictMode>,
);
